import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CheckSquare, Send, Pencil, Trash2, Clock, Loader2, RefreshCw, BarChart3, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ernaehrungAPI, freigabenAPI } from '@/lib/api';
import { AbschlussVorschau } from '@/components/ErnaehrungAbschluss';

/* Freigabe-Zentrale (v5, M4): eine Warteschlange fuer alle Entwuerfe aus
   Setter, Copilot, Funnel, spaeter Nachfass/Einschaetzung/Angebot.
   Links Kontext, rechts editierbarer Entwurf. Tastatur: J/K blaettern,
   Enter senden, V verwerfen, S spaeter. Ohne Klick geht nur raus, was unter /automatiken ausdruecklich auf
   „automatisch" steht (Probetraining-Bestaetigung, 05.10.2026); solche Eintraege tragen dann einen Hinweis,
   wenn die Automatik sie NICHT gesendet hat (automatik_hinweis).
   E10 (15.10.2026, Mockup CrmKiRisiko): Gruppe „Check-in-Auswertungen" — KI-Wochenauswertung je Check-in mit
   „Das Wichtigste" in drei Punkten, Plan-Vorschlag (übernehmen über den Ernährungs-Kern) und Antwortentwurf im
   Ton des Coachs, Kanal Coach-Chat. Senden = Nachricht im Coach-Chat der App; nichts geht ohne Klick raus. */

const GRUENDE = [
  ['unpassend', 'Unpassend'], ['falscher_ton', 'Falscher Ton'], ['falsche_fakten', 'Falsche Fakten'],
  ['schon_erledigt', 'Schon erledigt'], ['sonstiges', 'Sonstiges'],
];
const KANAL_FARBE = { whatsapp: 'bg-green-600', instagram: 'bg-pink-600', email: 'bg-blue-600', telefon: 'bg-amber-600', intern: 'bg-slate-500', push: 'bg-violet-600', coach_chat: 'bg-orange-600' };
const KANAL_LABEL = { coach_chat: 'Coach-Chat', push: 'Push' };
const TYP_LABEL = { antwort: 'Antwort', nachfass: 'Nachfass', aktion: 'Aktion', angebot: 'Angebot', bestaetigung: 'Bestätigung', erinnerung: 'Erinnerung', abschluss: 'Abschluss', buchung_ueber_kontingent: 'Kursanfrage', bewertung: 'Bewertung', abrechnung: 'Abrechnung' };
const ABSCHNITT = { dringend: 'Dringend', liegt: 'Liegt schon länger', normal: 'Weitere' };
// 30.10.2026 (freigaben/schritte.py): schnelle Gründe für „Diesmal nicht“ und „Lead beenden“ — eigener Text geht immer
const NICHT_GRUENDE = ['Kunde im Urlaub', 'Meldet sich selbst', 'Gerade im Gespräch', 'Falscher Zeitpunkt'];
const ENDE_GRUENDE = ['Kein Interesse', 'Zu teuer', 'Woanders angemeldet', 'Nicht erreichbar', 'Falsche Nummer'];
const fortschrittPunkte = (fs) => (fs?.versuch && fs?.von ? `${'●'.repeat(Math.min(fs.versuch, fs.von))}${'○'.repeat(Math.max(0, fs.von - fs.versuch))} Versuch ${fs.versuch} von ${fs.von}` : null);
const PUNKT_LABEL = { laeuft: 'Läuft', problem: 'Problem', auffaellig: 'Auffällig' };
const istKiAuswertung = (f) => f?.bezug?.art === 'ki_auswertung';
// E23 (15.10.2026): Abschluss Ernährungscoaching — „Senden“ heißt hier Bestätigen (Einwilligung, Abo, Formulare, Sequenz, Startgebühr,
// bei Stripe der Zahlungslink); nur die Leitung darf das, der Server lehnt sonst mit 403 ab. Verwerfen = Entwurf verworfen.
const istAbschluss = (f) => f?.bezug?.art === 'ernaehrung_abschluss';
// Kurskontingent (16.10.2026, backend/kurskontingent.py): Kursbuchung über dem Wochenkontingent — Freigeben bucht, Ablehnen sagt ab
const istKursanfrage = (f) => f?.bezug?.art === 'kursbuchung';
// Pia-Finanzen (02.11.2026, backend/pia_finanzen/vormerkungen.py): Zubuchung, Nachberechnung, Korrektur — nur der Inhaber sieht und gibt frei.
// Freigeben bucht über die bestehende Stelle (Einmalposten, Nachberechnung als Vorschlag im Rechnungslauf, Korrektur-Aufgabe); ab der
// Betragsgrenze erst nach dem Haken „Betrag geprüft“ (der Server prüft es selbst).
const istAbrechnung = (f) => f?.bezug?.art === 'pia_vormerkung' || f?.bezug?.art === 'pia_kontoaenderung';
const euro = (w) => (w == null ? '–' : `${Number(w).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
function kontingentText(k) {
  if (!k || k.unbegrenzt) return k ? `${k.genutzt ?? 0} gebucht, kein Limit` : '–';
  return `${k.genutzt} von ${k.je_woche} genutzt${k.nachholbar ? `, ${k.nachholbar} nachholbar` : ''}${k.warteliste ? `, ${k.warteliste} auf der Warteliste` : ''}`;
}

function zeit(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/* Linke Spalte einer Check-in-Auswertung: „Das Wichtigste", Plan-Vorschlag mit Diff und Übernehmen, Sicherheitshinweis. */
function KiAuswertungKontext({ eintrag, ergebnis, onUebernehmen, arbeitet }) {
  const k = eintrag.kontext || {};
  const v = k.plan_vorschlag;
  const sensibel = k.sicherheit === 'persoenlich_ansprechen';
  return (
    <div className="space-y-3" data-testid="ki-auswertung">
      <div className="rounded-md bg-muted p-3 text-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Das Wichtigste</div>
        <ol className="space-y-1.5 list-decimal pl-4">
          {(k.zusammenfassung || []).map((p) => <li key={p.art}><span className="font-semibold">{PUNKT_LABEL[p.art] || p.art}:</span> {p.text}</li>)}
        </ol>
      </div>
      {sensibel && (
        <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs" data-testid="ki-sensibel">
          Kein Entwurf: Die Antworten deuten auf ein sensibles Thema hin — bitte persönlich ansprechen. „Senden" hakt nur ab, es geht nichts an das Mitglied.
        </div>
      )}
      {v && (
        <div className="rounded-md border p-3 text-sm space-y-1.5" data-testid="ki-plan-vorschlag">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Plan-Vorschlag</div>
          <div className="font-medium">{v.text}</div>
          {v.begruendung && <div className="text-xs text-muted-foreground">{v.begruendung}</div>}
          {ergebnis ? (
            <div className="text-xs space-y-0.5">
              <div className="font-medium text-emerald-700">{ergebnis.angewendet ? 'Übernommen' : 'Vorschau'}</div>
              {(ergebnis.diff || []).slice(0, 8).map((d, i) => (
                <div key={i}>{d.datum ? `${d.datum.slice(8, 10)}.${d.datum.slice(5, 7)}. ` : ''}{d.was}: {d.rezept_vorher && d.rezept_vorher !== d.rezept_nachher ? `${d.rezept_vorher} → ${d.rezept_nachher}, ` : ''}{d.vorher} → {d.nachher}{d.einheit ? ` ${d.einheit}` : ''}</div>
              ))}
              {(ergebnis.diff || []).length > 8 && <div className="text-muted-foreground">… und {(ergebnis.diff || []).length - 8} weitere Tage</div>}
              {(ergebnis.hinweise || []).map((h, i) => <div key={i} className="text-muted-foreground">{h}</div>)}
            </div>
          ) : null}
          {!ergebnis?.angewendet && (
            <Button size="sm" variant="outline" disabled={arbeitet} onClick={onUebernehmen} data-testid="ki-plan-uebernehmen">Plan-Änderung übernehmen</Button>
          )}
        </div>
      )}
      <div className="text-xs text-muted-foreground">KI-Wochenauswertung{k.woche ? ` · ${k.woche}` : ''}{k.ausloeser === 'sonntag' ? ' · ohne Check-in (Sonntag)' : ''} — nur Aggregate ohne Namen an das Modell.</div>
    </div>
  );
}

export default function FreigabePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?id=fg_… (z. B. „Senden (Freigabe)" auf /heute): diesen Eintrag direkt
  // aufschlagen — auch wenn er hinter dem Listenlimit liegt. Wird einmal verbraucht.
  // Manche Sprünge (Zeitstrahl, Einschätzung, Abschluss) geben das Ziel als Router-State mit statt in der Adresse.
  const location = useLocation();
  const zielId = useRef(searchParams.get('id') || location.state?.freigabe_id || null);
  const [eintraege, setEintraege] = useState([]);
  const [laden, setLaden] = useState(true);
  const [aktiv, setAktiv] = useState(0);
  const [text, setText] = useState('');
  const [filter, setFilter] = useState({ mandant: '', typ: '', kanal: '', status: 'offen' });
  const [arbeitet, setArbeitet] = useState(false);
  const [verwerfenOffen, setVerwerfenOffen] = useState(false);
  // Rundschreiben gehen an viele: erst Rückfrage mit Empfängerzahl, nie per Enter (05.10.2026)
  const [rundschreibenFrage, setRundschreibenFrage] = useState(false);
  const [statistik, setStatistik] = useState(null);
  // E10: Gruppe „Check-in-Auswertungen" (nur diese) und das Ergebnis einer übernommenen Plan-Änderung je Auswertung
  const [gruppe, setGruppe] = useState('alle');
  const [planErgebnis, setPlanErgebnis] = useState({});
  // 30.10.2026: „Schon selbst gesendet“ fragt nach; Kunden-Entwürfe haben vier Knöpfe (aktion = offenes Feld darunter)
  const [selbstFrage, setSelbstFrage] = useState(false);
  const [aktion, setAktion] = useState(null);           // null | 'anders' | 'nicht' | 'beenden'
  const [grund, setGrund] = useState('');
  const [verlauf, setVerlauf] = useState([]);
  const [wartet, setWartet] = useState(0);
  const [betragOk, setBetragOk] = useState(false);      // Pia-Finanzen: zweiter Klick ab der Betragsgrenze
  const [bilanz, setBilanz] = useState(null);
  const [bilanzOffen, setBilanzOffen] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
      const r = await freigabenAPI.liste(params);
      let liste = r.data.eintraege || [];
      setWartet(r.data.wartet || 0);
      freigabenAPI.bilanz().then((b) => setBilanz(b.data)).catch(() => setBilanz(null));
      const ziel = zielId.current;
      if (ziel) {
        zielId.current = null;
        let idx = liste.findIndex((f) => f.id === ziel);
        if (idx < 0) {
          try {
            const e = await freigabenAPI.einzeln(ziel);
            const doc = e.data?.freigabe || e.data;
            if (doc?.id && doc.status === 'offen') { liste = [doc, ...liste]; idx = 0; }
            else toast.info('Dieser Entwurf ist nicht mehr offen');
          } catch { toast.error('Entwurf nicht gefunden'); }
        }
        setEintraege(liste);
        if (idx >= 0) { setAktiv(idx); return; }
      } else {
        setEintraege(liste);
      }
      setAktiv((a) => Math.min(a, Math.max(0, liste.length - 1)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Freigaben konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, [filter]);

  useEffect(() => { lade(); }, [lade]);
  const kiAnzahl = useMemo(() => eintraege.filter(istKiAuswertung).length, [eintraege]);
  const abschlussAnzahl = useMemo(() => eintraege.filter(istAbschluss).length, [eintraege]);
  const kursAnzahl = useMemo(() => eintraege.filter(istKursanfrage).length, [eintraege]);
  const abrechnungAnzahl = useMemo(() => eintraege.filter(istAbrechnung).length, [eintraege]);
  const gruppiert = useMemo(() => (gruppe === 'ki' ? eintraege.filter(istKiAuswertung) : gruppe === 'abschluss' ? eintraege.filter(istAbschluss)
    : gruppe === 'kurs' ? eintraege.filter(istKursanfrage) : gruppe === 'abrechnung' ? eintraege.filter(istAbrechnung) : eintraege), [eintraege, gruppe]);
  const dringendAnzahl = useMemo(() => eintraege.filter((f) => f.dringlichkeit === 'dringend').length, [eintraege]);
  const liste = gruppiert;
  useEffect(() => { setAktiv((a) => Math.min(a, Math.max(0, liste.length - 1))); }, [liste.length]);
  const aktuell = liste[aktiv];
  useEffect(() => {
    setText(aktuell?.entwurf?.text || ''); setVerwerfenOffen(false); setRundschreibenFrage(false); setSelbstFrage(false);
    setAktion(null); setGrund(''); setVerlauf([]); setBetragOk(false);
    if (aktuell?.id && (aktuell.lead_id || aktuell.bezug?.lead_id)) {
      freigabenAPI.verlauf(aktuell.id).then((r) => setVerlauf(r.data?.eintraege || [])).catch(() => setVerlauf([]));
    }
  }, [aktuell?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const istKunde = aktuell?.knoepfe?.modus === 'kunde';
  const istEnde = aktuell?.bezug?.art === 'nachfass_ende';     // Entscheidung nach drei Versuchen: nur per Klick
  const istRundschreiben = aktuell?.bezug?.art === 'rundschreiben';
  const istKi = istKiAuswertung(aktuell);
  const istAbschl = istAbschluss(aktuell);
  const istKurs = istKursanfrage(aktuell);
  const istAbr = istAbrechnung(aktuell);
  const sendenLabel = aktuell?.knoepfe?.senden?.label || 'Senden';

  const jetztUeberfaellig = useMemo(() => new Date().toISOString(), [eintraege]);

  const naechste = () => setAktiv((a) => Math.min(a + 1, Math.max(0, liste.length - 1)));
  const vorherige = () => setAktiv((a) => Math.max(a - 1, 0));

  const planUebernehmen = async () => {
    const aid = aktuell?.kontext?.auswertung_id;
    if (!aid || arbeitet) return false;
    setArbeitet(true);
    try {
      const r = await ernaehrungAPI.kiPlanUebernehmen(aid);
      setPlanErgebnis((p) => ({ ...p, [aid]: r.data }));
      toast.success('Plan-Änderung übernommen');
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Plan-Änderung nicht übernommen'); return false; }
    finally { setArbeitet(false); }
  };

  const senden = async (bestaetigt = false) => {
    if (!aktuell || arbeitet) return;
    if (istRundschreiben && bestaetigt !== true) { setRundschreibenFrage(true); return; }
    setRundschreibenFrage(false);
    setArbeitet(true);
    try {
      const r = await freigabenAPI.freigeben(aktuell.id, text !== aktuell.entwurf?.text ? text : undefined, istAbrechnung(aktuell) && betragOk ? { betrag_bestaetigt: true } : {});
      const d = r.data || {};
      toast.success(d.abrechnung ? (d.abrechnung.typ === 'zubuchung' ? `Zugebucht auf die Rechnung ${d.abrechnung.monat}` : d.abrechnung.typ === 'nachberechnung' ? 'Nachberechnung liegt im Rechnungslauf' : 'Aufgabe für die Korrektur angelegt')
        : d.abschluss ? 'Abschluss bestätigt — Abo angelegt'
        : d.buchung ? (d.buchung.status === 'waitlist' ? `Freigegeben — auf der Warteliste (Platz ${d.buchung.waitlist_position ?? '?'})` : 'Freigegeben — Platz gebucht')
        : d.status === 'gesendet' ? `Gesendet (${d.versandweg})` : (d.hinweis || 'Freigegeben'));
      await lade();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Senden fehlgeschlagen');
      // Nach „Versand unklar“ trägt die Freigabe jetzt einen Hinweis: neu laden, damit er sichtbar ist und Enter sperrt.
      // Denselben Eintrag wieder aufschlagen, auch wenn sich die Liste inzwischen verschoben hat.
      zielId.current = aktuell.id;
      await lade();
    }
    finally { setArbeitet(false); }
  };
  const verwerfen = async (grund) => {
    if (!aktuell || arbeitet) return;
    setArbeitet(true);
    try { await freigabenAPI.verwerfen(aktuell.id, grund); toast.success('Verworfen'); setVerwerfenOffen(false); await lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verwerfen fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };
  const spaeter = async (stunden = 4) => {
    if (!aktuell || arbeitet) return;
    setArbeitet(true);
    try {
      const bis = new Date(Date.now() + stunden * 3600 * 1000).toISOString();
      await freigabenAPI.zurueckstellen(aktuell.id, bis); toast.success(`Zurückgestellt bis ${zeit(bis)}`); await lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Zurückstellen fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };
  const manuell = async () => {
    if (!aktuell || arbeitet) return;
    setArbeitet(true);
    try { await freigabenAPI.manuellGesendet(aktuell.id); toast.success('Als selbst gesendet eingetragen'); setSelbstFrage(false); await lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Abhaken fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };
  // 30.10.2026: Anders erledigt · Diesmal nicht · Lead beenden (freigaben/schritte.py)
  const schritt = async (art) => {
    if (!aktuell || arbeitet) return;
    const g = grund.trim();
    if (art !== 'anders' && g.length < 3) { toast.error('Bitte kurz den Grund angeben'); return; }
    setArbeitet(true);
    try {
      if (art === 'nicht') {
        const r = await freigabenAPI.diesmalNicht(aktuell.id, g);
        toast.success(`Kommt am ${zeit(r.data?.wieder_am)} wieder${r.data?.nachfass ? ' – zählt nicht als Versuch' : ''}`);
      } else if (art === 'beenden') {
        await freigabenAPI.leadBeenden(aktuell.id, g);
        toast.success('Lead beendet (verloren)');
      }
      setAktion(null); setGrund(''); await lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    finally { setArbeitet(false); }
  };
  const andersErledigt = async (art) => {
    if (!aktuell || arbeitet) return;
    setArbeitet(true);
    try {
      await freigabenAPI.andersErledigt(aktuell.id, art, grund.trim() || undefined);
      toast.success(art === 'selbst_geschrieben' ? 'Als selbst geschrieben eingetragen' : 'Anruf eingetragen');
      setAktion(null); setGrund(''); await lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    finally { setArbeitet(false); }
  };
  const ladeStatistik = async () => {
    try { setStatistik((await freigabenAPI.statistik({})).data); } catch { toast.error('Statistik nicht verfügbar'); }
  };

  // Tastatur: nur, wenn nicht im Textfeld getippt wird
  useEffect(() => {
    const h = (e) => {
      const fokus = document.activeElement?.tagName;
      const inFeld = ['TEXTAREA', 'INPUT'].includes(fokus);
      if (inFeld && !(e.key === 'Enter' && (e.metaKey || e.ctrlKey))) return;
      // Auswahlfelder und Knöpfe behalten ihre eigenen Tasten; Kürzel mit Cmd/Strg/Alt gehören dem Browser
      if (!inFeld && (['SELECT', 'BUTTON', 'A'].includes(fokus) || e.metaKey || e.ctrlKey || e.altKey)) return;
      if (e.key === 'j' || e.key === 'J') naechste();
      else if (e.key === 'k' || e.key === 'K') vorherige();
      else if (e.key === 'Enter') {
        e.preventDefault();
        // Ein Eintrag mit Hinweis („Versand unklar“, „Kunde hat geschrieben“ …) geht nie per Taste raus
        if (aktuell?.automatik_hinweis) toast.message('Bitte erst den Hinweis lesen und dann mit dem Knopf senden');
        else if (!istRundschreiben && !istAbschl && !istEnde && !istAbr) senden();          // Abschlüsse und Abrechnungen (Geld) nur per Klick
      }
      else if (e.key === 'v' || e.key === 'V') { if (istKunde) { if (aktuell?.knoepfe?.diesmal_nicht) setAktion('nicht'); } else setVerwerfenOffen(true); }
      else if ((e.key === 's' || e.key === 'S') && !istKunde) spaeter();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="freigabe-page">
      <div className="flex flex-wrap items-center gap-2">
        <CheckSquare className="h-5 w-5" />
        <h1 className="text-xl font-bold">Freigabe-Zentrale</h1>
        <Badge variant={eintraege.length ? 'default' : 'secondary'}>{eintraege.length} offen</Badge>
        {dringendAnzahl > 0 && <Badge variant="destructive" data-testid="freigabe-dringend">{dringendAnzahl} dringend</Badge>}
        {wartet > 0 && <Badge variant="outline" title="„Diesmal nicht“ – kommen an ihrem Tag von selbst wieder">{wartet} kommen später wieder</Badge>}
        <span className="text-xs text-muted-foreground hidden sm:inline">Nichts geht ohne deinen Klick raus</span>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          {[['mandant', ['', 'p1', 'ptl', 'zinzino'], 'Alle Mandanten'],
            ['typ', ['', 'antwort', 'nachfass', 'bestaetigung', 'erinnerung', 'angebot', 'aktion', 'abschluss', 'bewertung', 'buchung_ueber_kontingent', 'abrechnung'], 'Alle Typen'],
            ['kanal', ['', 'whatsapp', 'instagram', 'email', 'telefon', 'intern', 'push', 'coach_chat'], 'Alle Kanäle'],
            ['status', ['offen', 'gesendet', 'verworfen', 'freigegeben', 'alle'], null]].map(([feld, werte, leer]) => (
            <select key={feld} value={filter[feld]} onChange={(e) => setFilter({ ...filter, [feld]: e.target.value })}
                    className="h-8 rounded-md border border-border bg-background px-2 text-xs">
              {werte.map((w) => <option key={w} value={w}>{w === '' ? leer : (TYP_LABEL[w] || KANAL_LABEL[w] || w)}</option>)}
            </select>
          ))}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={lade} title="Neu laden"><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={ladeStatistik} title="Statistik"><BarChart3 className="h-4 w-4" /></Button>
        </div>
      </div>

      {bilanz && (
        <div className="rounded-md border border-border p-2 text-xs space-y-1" data-testid="freigabe-bilanz">
          <button type="button" className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left" onClick={() => setBilanzOffen((o) => !o)}>
            <span className="font-medium">{bilanz.satz}</span>
            <span className={`ml-auto font-medium ${bilanz.ohne_kontakt?.anzahl ? 'text-red-600' : 'text-emerald-600'}`} data-testid="ohne-kontakt">
              Ohne Kontakt verloren (30 Tage): {bilanz.ohne_kontakt?.anzahl ?? '–'}
            </span>
          </button>
          {bilanzOffen && (
            <div className="space-y-1 pt-1">
              {Object.entries(bilanz.personen || {}).map(([p, z]) => (
                <div key={p}><span className="font-medium">{p}</span>: {z.gesendet} gesendet · {z.angerufen} angerufen · {z.selbst} selbst · {z.uebersprungen} übersprungen · {z.beendet} beendet</div>
              ))}
              {(bilanz.gruende || []).length > 0 && <div className="text-muted-foreground">Übersprungen weil: {bilanz.gruende.join(' · ')}</div>}
              {(bilanz.ohne_kontakt?.leads || []).length > 0 && (
                <div className="text-muted-foreground">Ohne Kontakt verloren:{' '}
                  {bilanz.ohne_kontakt.leads.map((l, i) => (
                    <span key={l.id}>{i > 0 && ', '}<button type="button" className="underline" onClick={() => navigate(`/leads/${l.id}`)}>{l.name || 'Lead'}</button></span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {statistik && (
        <Card><CardContent className="p-3 text-xs space-y-1">
          <div className="font-medium">Statistik — {statistik.gesamt} Freigaben, {statistik.offen} offen</div>
          {Object.entries(statistik.je_quelle || {}).map(([q, s]) => (
            <div key={q}>
              <span className="font-medium capitalize">{q}</span>: {s.gesendet} gesendet · {s.verworfen} verworfen · {s.offen} offen ·
              Quote {s.freigabequote == null ? '–' : Math.round(s.freigabequote * 100) + ' %'} · Median {s.median_bearbeitung_min ?? '–'} min
              {Object.keys(s.gruende || {}).length > 0 && <span className="text-muted-foreground"> · Gründe: {Object.entries(s.gruende).map(([g, n]) => `${g} ${n}`).join(', ')}</span>}
            </div>
          ))}
        </CardContent></Card>
      )}

      {/* E10: Gruppen — alles oder nur die Check-in-Auswertungen der KI-Wochenauswertung; E23: Gruppe „Abschlüsse“ */}
      {(kiAnzahl > 0 || abschlussAnzahl > 0 || kursAnzahl > 0 || abrechnungAnzahl > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid="freigabe-gruppen">
          {[['alle', `Alle ${eintraege.length}`], ...(kiAnzahl ? [['ki', `Check-in-Auswertungen ${kiAnzahl}`]] : []),
            ...(abschlussAnzahl ? [['abschluss', `Abschlüsse ${abschlussAnzahl}`]] : []),
            ...(kursAnzahl ? [['kurs', `Kursanfragen ${kursAnzahl}`]] : []),
            ...(abrechnungAnzahl ? [['abrechnung', `Abrechnung ${abrechnungAnzahl}`]] : [])].map(([g, l]) => (
            <button key={g} type="button" onClick={() => { setGruppe(g); setAktiv(0); }}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${gruppe === g ? 'border-foreground bg-foreground text-background' : 'border-border bg-background hover:bg-muted/50'}`}>
              {g === 'ki' && <Sparkles className="inline h-3 w-3 mr-1" />}{l}
            </button>
          ))}
        </div>
      )}

      {laden ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Lädt …</div>
        : liste.length === 0 ? (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Nichts offen. Neue Entwürfe erscheinen hier, sobald Setter, Copilot, Funnel oder die KI-Wochenauswertung etwas formulieren.</CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-4">
            <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
              {liste.map((f, i) => (
                <div key={f.id}>
                {(i === 0 || liste[i - 1].dringlichkeit !== f.dringlichkeit) && f.dringlichkeit && (
                  <div className={`text-[11px] font-semibold uppercase tracking-wide px-1 ${i ? 'pt-3' : ''} pb-1 ${f.dringlichkeit === 'dringend' ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {ABSCHNITT[f.dringlichkeit]}
                  </div>
                )}
                <button onClick={() => setAktiv(i)}
                        className={`w-full text-left rounded-md border p-2 text-xs ${i === aktiv ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${KANAL_FARBE[f.kanal] || 'bg-slate-400'}`} />
                    <span className="font-medium truncate">{f.titel}</span>
                    {f.automatik_hinweis && <span className="ml-auto shrink-0 text-amber-600" title={f.automatik_hinweis} data-testid="hinweis-marke">⚠</span>}
                  </div>
                  <div className="text-muted-foreground mt-0.5 flex gap-2">
                    <span>{TYP_LABEL[f.typ] || f.typ} · {f.quelle}</span>
                    <span className={f.frist_am < jetztUeberfaellig ? 'text-red-600 font-medium' : ''}>{zeit(f.frist_am)}</span>
                  </div>
                  {istKiAuswertung(f) && (
                    <div className="mt-0.5 text-muted-foreground truncate">
                      {f.kontext?.sicherheit === 'persoenlich_ansprechen' ? 'Sensibel — persönlich ansprechen' : (f.kontext?.zusammenfassung?.[1]?.text || f.kontext?.zusammenfassung?.[0]?.text || '')}
                    </div>
                  )}
                  {f.dringlich_grund && (
                    <div className={`mt-0.5 truncate ${f.dringlichkeit === 'dringend' ? 'text-red-600 font-medium' : f.dringlichkeit === 'liegt' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{f.dringlich_grund}</div>
                  )}
                  {(fortschrittPunkte(f.fortschritt) || f.weitere) && (
                    <div className="mt-0.5 flex gap-2 text-muted-foreground">
                      {fortschrittPunkte(f.fortschritt) && <span className="tracking-wide">{fortschrittPunkte(f.fortschritt)}</span>}
                      {f.weitere > 0 && <span>+{f.weitere} weiterer Entwurf</span>}
                    </div>
                  )}
                </button>
                </div>
              ))}
            </div>

            {aktuell && (
              <Card><CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge className={KANAL_FARBE[aktuell.kanal]}>{KANAL_LABEL[aktuell.kanal] || aktuell.kanal}</Badge>
                  <Badge variant="outline">{TYP_LABEL[aktuell.typ] || aktuell.typ}</Badge>
                  <Badge variant="outline">{aktuell.quelle}</Badge>
                  <Badge variant="outline">{aktuell.mandant}</Badge>
                  {aktuell.status !== 'offen' && <Badge variant="secondary">{aktuell.status}</Badge>}
                  <span className="text-muted-foreground ml-auto">Frist {zeit(aktuell.frist_am)} · {aktiv + 1}/{liste.length}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="text-sm space-y-2">
                    <div className="font-medium">{aktuell.titel}</div>
                    {aktuell.automatik_hinweis && ['offen', 'zurueckgestellt'].includes(aktuell.status) && (
                      <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs" data-testid="automatik-hinweis">
                        <span className="font-medium">Bitte prüfen:</span> {aktuell.automatik_hinweis}
                      </div>
                    )}
                    {istKi && (
                      <KiAuswertungKontext eintrag={aktuell} ergebnis={planErgebnis[aktuell.kontext?.auswertung_id]} onUebernehmen={planUebernehmen} arbeitet={arbeitet} />
                    )}
                    {istAbschl && (
                      <div className="space-y-2" data-testid="abschluss-kontext">
                        <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs">
                          <span className="font-medium">Nur die Leitung bestätigt.</span> Bestätigen legt Einwilligung, Abo, Formulare und Sequenz an
                          {aktuell.kontext?.abschluss?.zahlweg === 'stripe' ? ' und schickt den Zahlungslink' : ' — die Startgebühr und der Startmonat kommen auf die Monatsrechnung'}.
                          Bis dahin wird nichts gesendet und nichts abgerechnet.
                        </div>
                        <AbschlussVorschau v={aktuell.kontext?.vorschau} />
                      </div>
                    )}
                    {istAbr && aktuell.kontext?.kontoaenderung && (
                      <div className="rounded-md border border-border p-2 text-xs space-y-1" data-testid="kontoaenderung-kontext">
                        <div><span className="text-muted-foreground">Kontoänderung:</span> {aktuell.kontext.kontoaenderung.mitglied_name} · {aktuell.kontext.kontoaenderung.vertragsnummer}</div>
                        <div><span className="text-muted-foreground">Neu:</span> {aktuell.kontext.kontoaenderung.kontoinhaber} · IBAN {aktuell.kontext.kontoaenderung.iban_neu}</div>
                        <div><span className="text-muted-foreground">Bisher:</span> IBAN {aktuell.kontext.kontoaenderung.iban_alt || '–'}</div>
                        <div className="text-muted-foreground">Vom Mitglied selbst eingegeben und unterschrieben ({aktuell.kontext.kontoaenderung.weg === 'link' ? 'per Link' : 'im Studio'}).</div>
                        {aktuell.kontext.einzug_hinweis && <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2">{aktuell.kontext.einzug_hinweis}</div>}
                        <div className="text-muted-foreground pt-1">Freigeben legt das neue SEPA-Mandat an (neue Referenz, Erstlastschrift), das alte endet, offene Rechnungen ziehen mit, das Mitglied bekommt den Vertrag mit Nachtrag. Verwerfen löscht die eingegebene Bankverbindung.</div>
                      </div>
                    )}
                    {istAbr && !aktuell.kontext?.kontoaenderung && (
                      <div className="rounded-md border border-border p-2 text-xs space-y-1" data-testid="abrechnung-kontext">
                        <div><span className="text-muted-foreground">Art:</span> {{ zubuchung: 'Zubuchung auf die nächste Rechnung', nachberechnung: 'Nachberechnung (Vorschlag im Rechnungslauf)', korrektur: 'Korrektur (wird eine Aufgabe für dich)' }[aktuell.kontext?.vormerkung?.typ] || '–'}</div>
                        <div><span className="text-muted-foreground">Mitglied:</span> {aktuell.kontext?.vormerkung?.mitglied_name}</div>
                        <div><span className="text-muted-foreground">Betrag:</span> {euro(aktuell.kontext?.vormerkung?.betrag)}{aktuell.kontext?.vormerkung?.typ === 'nachberechnung' ? ' (Schätzung, der Rechnungslauf rechnet den ersten Monat ggf. anteilig)' : ''}</div>
                        {(aktuell.kontext?.vormerkung?.leistungsmonate || []).length > 0 && <div><span className="text-muted-foreground">Monate:</span> {aktuell.kontext.vormerkung.leistungsmonate.join(', ')}</div>}
                        {aktuell.kontext?.vormerkung?.abrechnungsmonat && aktuell.kontext?.vormerkung?.typ === 'zubuchung' && <div><span className="text-muted-foreground">Rechnung:</span> {aktuell.kontext.vormerkung.abrechnungsmonat}</div>}
                        <div className="text-muted-foreground">Angelegt von {aktuell.kontext?.erstellt_von || '–'} · {aktuell.quelle === 'abbuchungswunsch' ? 'Abbuchungswunsch des Kunden' : 'aus Pia'}</div>
                        {aktuell.kontext?.ueber_grenze && (
                          <label className="flex items-center gap-2 rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2" data-testid="abrechnung-betrag-ok">
                            <input type="checkbox" checked={betragOk} onChange={(e) => setBetragOk(e.target.checked)} />
                            <span>Betrag {euro(aktuell.kontext?.vormerkung?.betrag)} geprüft (über der Grenze von {euro(aktuell.kontext?.betragsgrenze)})</span>
                          </label>
                        )}
                        <div className="text-muted-foreground pt-1">Freigeben bucht jetzt; der Text wird nicht verschickt. Verwerfen = nichts wird berechnet.</div>
                      </div>
                    )}
                    {istKurs && (
                      <div className="rounded-md border border-border p-2 text-xs space-y-0.5" data-testid="kursanfrage-kontext">
                        <div><span className="text-muted-foreground">Mitglied:</span> {aktuell.kontext?.name}</div>
                        <div><span className="text-muted-foreground">Kurs:</span> {aktuell.kontext?.kurs}</div>
                        <div><span className="text-muted-foreground">Termin:</span> {aktuell.kontext?.termin_lokal}</div>
                        <div><span className="text-muted-foreground">Kontingent:</span> {kontingentText(aktuell.kontext?.kontingent)}</div>
                        {aktuell.kontext?.wechsel && <div className="text-amber-700 dark:text-amber-400">Zweiter Wechsel des festen Platzes in dieser Woche</div>}
                        {aktuell.bezug?.join_waitlist && <div className="text-muted-foreground">Bei vollem Kurs auf die Warteliste</div>}
                        <div className="text-muted-foreground pt-1">Freigeben bucht den Platz (bei vollem Kurs ohne Warteliste bleibt die Anfrage offen). Ablehnen schickt dem Mitglied eine kurze Absage.</div>
                      </div>
                    )}
                    {aktuell.kontext?.letzte_nachricht && (
                      <div className="rounded-md bg-muted p-2 text-xs"><div className="text-muted-foreground mb-1">Letzte Nachricht</div>{aktuell.kontext.letzte_nachricht}</div>
                    )}
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {aktuell.kontext?.telefon && <div>Telefon: {aktuell.kontext.telefon}</div>}
                      {aktuell.kontext?.kurs && !istKurs && <div>Kurs: {aktuell.kontext.kurs}{aktuell.kontext.termin_lokal ? ` · ${aktuell.kontext.termin_lokal}` : ''}</div>}
                      {aktuell.kontext?.stufe && <div>Stufe: {aktuell.kontext.stufe}</div>}
                      {Array.isArray(aktuell.kontext?.offene_felder) && aktuell.kontext.offene_felder.length > 0 && <div>Offen: {aktuell.kontext.offene_felder.join(', ')}</div>}
                    </div>
                    <div className="flex gap-2 text-xs">
                      {istKi && aktuell.lead_id && <button className="underline" onClick={() => navigate(`/ernaehrung/${aktuell.lead_id}`)}>Entwicklung öffnen</button>}
                      {aktuell.lead_id && <button className="underline" onClick={() => navigate(`/leads/${aktuell.lead_id}`)}>Lead öffnen</button>}
                      {aktuell.konversation_id && <button className="underline" onClick={() => navigate('/posteingang')}>Posteingang</button>}
                      {aktuell.kontext?.whatsapp_link && <a className="underline" href={aktuell.kontext.whatsapp_link} target="_blank" rel="noopener noreferrer">WhatsApp öffnen</a>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {aktuell.entwurf?.betreff && <div className="text-xs text-muted-foreground">Betreff: {aktuell.entwurf.betreff}</div>}
                    {istKi && aktuell.kanal === 'coach_chat' && <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Antwortentwurf in deinem Ton — geht als Nachricht in den Coach-Chat der App</div>}
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={istKurs ? 4 : 10} readOnly={(istKi && aktuell.kanal === 'intern') || istAbschl || istKurs || istEnde || istAbr}
                              className="w-full text-sm rounded-md border border-border bg-background p-2 font-normal" />
                    <div className="flex flex-wrap gap-2">
                      {istKi && aktuell.kontext?.plan_vorschlag && !planErgebnis[aktuell.kontext?.auswertung_id]?.angewendet && aktuell.kanal === 'coach_chat' && (
                        <Button onClick={async (e) => { e.currentTarget.blur(); if (await planUebernehmen()) senden(); }} disabled={arbeitet || !['offen', 'zurueckgestellt'].includes(aktuell.status)} data-testid="freigabe-senden-plan">
                          <Send className="h-4 w-4" /><span className="ml-1">Senden + Plan übernehmen</span>
                        </Button>
                      )}
                      <Button variant={istKi && aktuell.kontext?.plan_vorschlag && aktuell.kanal === 'coach_chat' ? 'outline' : 'default'} onClick={(e) => { e.currentTarget.blur(); senden(); }} disabled={arbeitet || !['offen', 'zurueckgestellt'].includes(aktuell.status) || (istAbr && aktuell.kontext?.ueber_grenze && !betragOk)} data-testid="freigabe-senden">
                        {arbeitet ? <Loader2 className="h-4 w-4 animate-spin" /> : text !== aktuell.entwurf?.text ? <Pencil className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                        <span className="ml-1">{istAbschl ? 'Bestätigen' : istKurs || istAbr ? 'Freigeben' : istKi && aktuell.kanal === 'intern' ? 'Gelesen, ich spreche es an' : istKi ? (text !== aktuell.entwurf?.text ? 'Bearbeiten & Senden' : 'Nur senden') : `${sendenLabel}${text !== aktuell.entwurf?.text ? ' (geändert)' : ''}`}</span>
                      </Button>
                      {istKunde ? (
                        <>
                          {(aktuell.knoepfe?.anders || []).length > 0 && (
                            <Button variant="outline" onClick={() => { setAktion((a) => (a === 'anders' ? null : 'anders')); setGrund(''); }} disabled={arbeitet} data-testid="freigabe-anders">
                              <CheckSquare className="h-4 w-4 mr-1" />Anders erledigt
                            </Button>
                          )}
                          {aktuell.knoepfe?.diesmal_nicht && (
                            <Button variant="outline" onClick={() => { setAktion((a) => (a === 'nicht' ? null : 'nicht')); setGrund(''); }} disabled={arbeitet} data-testid="freigabe-nicht">
                              <Clock className="h-4 w-4 mr-1" />Diesmal nicht
                            </Button>
                          )}
                          {aktuell.knoepfe?.beenden && (
                            <Button variant="outline" className="text-red-600" onClick={() => { setAktion((a) => (a === 'beenden' ? null : 'beenden')); setGrund(''); }} disabled={arbeitet} data-testid="freigabe-beenden">
                              <Trash2 className="h-4 w-4 mr-1" />Lead beenden
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {istKurs ? (
                            <Button variant="outline" onClick={() => verwerfen('sonstiges')} disabled={arbeitet || !['offen', 'zurueckgestellt'].includes(aktuell.status)} data-testid="kursanfrage-ablehnen">
                              <Trash2 className="h-4 w-4 mr-1" />Ablehnen
                            </Button>
                          ) : (
                            <Button variant="outline" onClick={() => setVerwerfenOffen((v) => !v)} disabled={arbeitet}><Trash2 className="h-4 w-4 mr-1" />Verwerfen</Button>
                          )}
                          <Button variant="outline" onClick={() => spaeter()} disabled={arbeitet}><Clock className="h-4 w-4 mr-1" />Später (4 h)</Button>
                          {aktuell.knoepfe?.selbst && ['offen', 'zurueckgestellt'].includes(aktuell.status) && (
                            <Button variant="ghost" onClick={() => setSelbstFrage((v) => !v)} disabled={arbeitet} data-testid="freigabe-selbst">Schon selbst gesendet</Button>
                          )}
                        </>
                      )}
                    </div>
                    {istKunde && aktion === 'anders' && (
                      <div className="rounded-md border border-border p-3 text-sm space-y-2" data-testid="anders-feld">
                        <div className="font-medium">Wie hast du den Kontakt erledigt?</div>
                        <div className="text-xs text-muted-foreground">{aktuell.knoepfe.anders_satz}</div>
                        <input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Notiz (freiwillig), z. B. kommt Donnerstag zum Probetraining"
                               className="w-full h-8 rounded-md border border-border bg-background px-2 text-xs" />
                        <div className="flex flex-wrap gap-2">
                          {aktuell.knoepfe.anders.map((o) => (
                            <Button key={o.art} size="sm" variant={o.art === 'angerufen_erreicht' ? 'default' : 'secondary'} onClick={() => andersErledigt(o.art)} disabled={arbeitet} data-testid={`anders-${o.art}`}>{o.label}</Button>
                          ))}
                          <Button size="sm" variant="ghost" onClick={() => setAktion(null)} disabled={arbeitet}>Abbrechen</Button>
                        </div>
                      </div>
                    )}
                    {istKunde && (aktion === 'nicht' || aktion === 'beenden') && (
                      <div className={`rounded-md border p-3 text-sm space-y-2 ${aktion === 'beenden' ? 'border-red-400' : 'border-border'}`} data-testid={`${aktion}-feld`}>
                        <div className="font-medium">{aktion === 'nicht' ? 'Warum diesmal nicht?' : 'Warum endet der Lead?'}</div>
                        <div className="text-xs text-muted-foreground">{aktion === 'nicht' ? aktuell.knoepfe.diesmal_nicht : aktuell.knoepfe.beenden}</div>
                        <div className="flex flex-wrap gap-1">
                          {(aktion === 'nicht' ? NICHT_GRUENDE : ENDE_GRUENDE).map((g) => (
                            <button key={g} type="button" onClick={() => setGrund(g)}
                                    className={`rounded-full border px-2 py-0.5 text-xs ${grund === g ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-muted/50'}`}>{g}</button>
                          ))}
                        </div>
                        <input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Grund (Pflicht)" data-testid="grund-feld"
                               className="w-full h-8 rounded-md border border-border bg-background px-2 text-xs" />
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant={aktion === 'beenden' ? 'destructive' : 'default'} onClick={() => schritt(aktion)} disabled={arbeitet || grund.trim().length < 3} data-testid={`${aktion}-ja`}>
                            {aktion === 'nicht' ? 'Diesmal nicht, später wieder' : 'Lead beenden'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setAktion(null)} disabled={arbeitet}>Abbrechen</Button>
                        </div>
                      </div>
                    )}
                    {!istKunde && selbstFrage && aktuell.knoepfe?.selbst && (
                      <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-2" data-testid="selbst-frage">
                        <div className="font-medium">Hast du die Nachricht wirklich schon selbst geschickt?</div>
                        <div className="text-xs text-muted-foreground">{aktuell.knoepfe.selbst}</div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={manuell} disabled={arbeitet} data-testid="selbst-ja">Ja, schon selbst gesendet</Button>
                          <Button size="sm" variant="outline" onClick={() => setSelbstFrage(false)} disabled={arbeitet}>Nein, abbrechen</Button>
                        </div>
                      </div>
                    )}
                    {aktuell.knoepfe && !istAbschl && !istKurs && !istKi && !istAbr && ['offen', 'zurueckgestellt'].includes(aktuell.status) && (
                      <details className="rounded-md bg-muted/50 p-2 text-xs" data-testid="was-passiert">
                        <summary className="cursor-pointer text-muted-foreground">Was passiert bei welchem Knopf?</summary>
                        <dl className="space-y-1 pt-1">
                          {(istKunde
                            ? [[sendenLabel, aktuell.knoepfe.senden?.satz], ['Anders erledigt', (aktuell.knoepfe.anders || []).length ? aktuell.knoepfe.anders_satz : null],
                               ['Diesmal nicht', aktuell.knoepfe.diesmal_nicht], ['Lead beenden', aktuell.knoepfe.beenden]]
                            : [[sendenLabel, aktuell.knoepfe.senden?.satz], ['Verwerfen', aktuell.knoepfe.verwerfen], ['Später', aktuell.knoepfe.spaeter]]
                          ).filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="flex gap-2"><dt className="font-semibold shrink-0 w-28">{k}</dt><dd className="text-muted-foreground min-w-0">{v}</dd></div>
                          ))}
                        </dl>
                      </details>
                    )}
                    {verlauf.length > 0 && (
                      <div className="rounded-md border border-border p-2 text-xs space-y-1" data-testid="freigabe-verlauf">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bisher mit diesem Lead</div>
                        {verlauf.map((v) => (
                          <div key={v.id} className="flex gap-2">
                            <span className="shrink-0 text-muted-foreground w-24">{zeit(v.am)}</span>
                            <span className="min-w-0"><span className="font-medium">{v.was}</span>{v.von ? ` · ${v.von}` : ''}{v.grund ? ` · ${v.grund}` : ''}
                              {v.titel ? <span className="text-muted-foreground"> · {v.titel}</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {rundschreibenFrage && istRundschreiben && (
                      <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-2" data-testid="rundschreiben-frage">
                        <div className="font-medium">Rundschreiben jetzt starten?</div>
                        <div className="text-xs text-muted-foreground">
                          Es geht an {aktuell.kontext?.empfaenger ?? 'alle'} Empfänger{aktuell.kontext?.kanal ? ` per ${aktuell.kontext.kanal === 'email' ? 'E-Mail' : aktuell.kontext.kanal === 'whatsapp' ? 'WhatsApp' : 'Push'}` : ''}. Das lässt sich nicht zurückholen.
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => senden(true)} disabled={arbeitet} data-testid="rundschreiben-ja">Ja, jetzt senden</Button>
                          <Button size="sm" variant="outline" onClick={() => setRundschreibenFrage(false)} disabled={arbeitet}>Abbrechen</Button>
                        </div>
                      </div>
                    )}
                    {verwerfenOffen && (
                      <div className="flex flex-wrap gap-1">
                        {GRUENDE.map(([k, l]) => <Button key={k} size="sm" variant="secondary" onClick={() => verwerfen(k)}>{l}</Button>)}
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground">Tastatur: J/K blättern · Enter senden{istRundschreiben ? ' (bei Rundschreiben nur per Klick)' : istAbschl ? ' (Abschlüsse nur per Klick)' : ''}{istEnde ? ' (hier nur per Klick)' : istKunde ? ' · V diesmal nicht' : ' · V verwerfen · S später'}</p>
                  </div>
                </div>
              </CardContent></Card>
            )}
          </div>
        )}
    </div>
  );
}
